import axios from "axios"

import { isEmpty } from "@/utils/typeOf"
import { createAccountStore } from "@/stores/account.ts"
import { ApiErrorResponse } from "./type"
import keycloakInstance from "@/providers/keycloak"

const BASE_URL = import.meta.env.VITE_API_ENDPOINT ?? ""

const excludeUrl = ["teams", "auth", "accounts", "stadiums", "util", "positions", "metric-group"]

const defaultOptions = {
  instanceName: "analyticsV2",
  baseURL: BASE_URL,
  timeout: 120000,
}

function isValidUrl(url: string) {
  const pureUrl = url.split("?")[0]

  return !pureUrl.split("/").slice(1).some(isEmpty)
}

let requestInterceptor: number | null = null
let responseInterceptor: number | null = null

// 토큰 갱신 중복 방지를 위한 변수
let isRefreshing = false
let failedQueue: Array<{
  resolve: (value?: unknown) => void
  reject: (reason?: unknown) => void
}> = []

const processQueue = (error: unknown = null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })

  failedQueue = []
}

export const API = axios.create(defaultOptions)

export function setInterCeptor(token?: string) {
  // 중복된 url 구성이 될수 있어 기존 인터셉터를 제거한다.
  if (requestInterceptor !== null) {
    API.interceptors.request.eject(requestInterceptor)
  }

  if (responseInterceptor !== null) {
    API.interceptors.response.eject(responseInterceptor)
  }

  requestInterceptor = API.interceptors.request.use(
    (config) => {
      // header에 accessToken 추가 (항상 최신 토큰 사용)
      const currentToken = keycloakInstance.token || token
      if (currentToken && config.headers) {
        config.headers.Authorization = `Bearer ${currentToken}`
      }

      // 권한 인가 확인을 위해 url에 teams/${idfTeam} 추가
      // React 밖이라, Store에 직접 접근
      const selectedIdfTeam = createAccountStore.getState().selectedIdfTeam

      const paths = config.url?.split("/").splice(1)
      if (selectedIdfTeam && !excludeUrl.includes(paths?.[0] as string)) {
        config.url = config.url?.replace(/\/teams\/\d+/, "")
        config.url = `/teams/${selectedIdfTeam}${config.url}`
      }

      const accountInfo = createAccountStore.getState().info
      if (accountInfo?.type === "P" && accountInfo?.idfAccount) {
        const playerIdsMatch = config.url?.match(/\/players\/([0-9,]+)/)
        if (playerIdsMatch) {
          const playerIdsString = playerIdsMatch[1]
          config.url = config.url?.replace(
            `/players/${playerIdsString}`,
            `/players/${accountInfo.idfAccount}`,
          )
        }
      }

      return config
    },
    (error) => {
      return Promise.reject(error.response?.data as ApiErrorResponse)
    },
  )

  responseInterceptor = API.interceptors.response.use(
    (value) => {
      return {
        success: true,
        error: null,
        response: value.config?.responseType === "blob" ? value : value?.data?.data || value.data,
      }
    },
    async (error) => {
      const originalRequest = error.config

      // 401 에러이고 인증된 상태인 경우 토큰 갱신 시도
      if (error.response?.status === 401 && keycloakInstance.authenticated) {
        // 이미 토큰 갱신 중이면 큐에 추가하고 대기
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject })
          })
            .then(() => {
              // 토큰 갱신 완료 후 재요청 (request interceptor에서 자동으로 최신 토큰 적용)
              return API.request(originalRequest)
            })
            .catch((err) => {
              return Promise.reject(err)
            })
        }

        // 첫 번째 401 에러인 경우 토큰 갱신 시작
        originalRequest._retry = true
        isRefreshing = true

        try {
          const refreshed = await keycloakInstance.updateToken(180)

          if (refreshed && keycloakInstance.token) {
            // 대기 중인 모든 요청에 새 토큰 전달
            processQueue(null, keycloakInstance.token)

            // 현재 요청 재시도 (request interceptor에서 자동으로 최신 토큰 적용)
            return API.request(originalRequest)
          }
        } catch (refreshError) {
          // 토큰 갱신 실패 시 대기 중인 모든 요청 거부
          processQueue(refreshError, null)
          console.error("Token refresh failed on 401:", refreshError)
          await keycloakInstance.logout({ redirectUri: window.location.origin })

          return Promise.reject(error.response?.data as ApiErrorResponse)
        } finally {
          isRefreshing = false
        }
      } else if (error.response?.status === 403) {
        const unauthorizedError: ApiErrorResponse = {
          error: {
            errorCode: 403,
            message: "Not authorized",
          },
          timestamp: new Date().toISOString(),
        }

        return Promise.reject(unauthorizedError)
      } else if (error.response?.status === 500) {
        const serverError: ApiErrorResponse = {
          error: {
            errorCode: 500,
            code: "INTERNAL_SERVER_ERROR",
          },
          timestamp: new Date().toISOString(),
        }

        return Promise.reject(serverError)
      }

      return Promise.reject(error.response?.data as ApiErrorResponse)
    },
  )
}
